import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { 
  setSharedProfileMode, 
  clearSharedProfileMode,
  setPublicProfileMode,
  clearPublicProfileMode 
} from '../lib/api';

const SharedProfileContext = createContext(null);

export function SharedProfileProvider({ children }) {
  const [isSharedProfileView, setIsSharedProfileView] = useState(false);
  const [sharedProfileData, setSharedProfileData] = useState(null);
  const [sharedProfileOwnerId, setSharedProfileOwnerId] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [currentShareId, setCurrentShareId] = useState(null);
  
  // Public profile mode state (new - persistent, user_id based)
  const [isPublicProfileView, setIsPublicProfileView] = useState(false);
  const [publicProfileUserId, setPublicProfileUserId] = useState(null);
  const [profileInfo, setProfileInfo] = useState(null);

  // Enter shared profile mode (legacy - share_id based)
  const enterSharedProfileMode = useCallback((shareId, data = null, ownerId = null, info = null) => {
    // Set shared profile mode in API layer (adds X-Shared-Profile-ID header to all requests)
    setSharedProfileMode(shareId);
    
    // Update context state
    setCurrentShareId(shareId);
    setIsSharedProfileView(true);
    setSharedProfileData(data);
    setSharedProfileOwnerId(ownerId);
    setShareInfo(info);
    
    // Clear public profile state
    setIsPublicProfileView(false);
    setPublicProfileUserId(null);
    setProfileInfo(null);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('[SharedProfile] Entered shared profile mode:', shareId);
    }
  }, []);

  // Exit shared profile mode (legacy)
  const exitSharedProfileMode = useCallback(() => {
    // Clear shared profile mode in API layer
    clearSharedProfileMode();
    
    // Reset context state
    setCurrentShareId(null);
    setIsSharedProfileView(false);
    setSharedProfileData(null);
    setSharedProfileOwnerId(null);
    setShareInfo(null);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('[SharedProfile] Exited shared profile mode');
    }
  }, []);

  // Enter public profile mode (new - persistent, user_id based)
  const enterPublicProfileMode = useCallback((userId, info = null) => {
    // Set public profile mode in API layer (adds X-Public-Profile-ID header to all requests)
    setPublicProfileMode(userId);
    
    // Update context state
    setPublicProfileUserId(userId);
    setIsPublicProfileView(true);
    setProfileInfo(info);
    
    // Also set the generic shared profile view flag (for component compatibility)
    setIsSharedProfileView(true);
    setSharedProfileOwnerId(userId);
    
    // Clear legacy share state
    setCurrentShareId(null);
    setShareInfo(null);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('[SharedProfile] Entered public profile mode:', userId);
    }
  }, []);

  // Exit public profile mode (new)
  const exitPublicProfileMode = useCallback(() => {
    // Clear public profile mode in API layer
    clearPublicProfileMode();
    
    // Reset context state
    setPublicProfileUserId(null);
    setIsPublicProfileView(false);
    setProfileInfo(null);
    
    // Also reset generic shared profile view flag
    setIsSharedProfileView(false);
    setSharedProfileOwnerId(null);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('[SharedProfile] Exited public profile mode');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Ensure API layer is reset when context unmounts
      clearSharedProfileMode();
      clearPublicProfileMode();
    };
  }, []);

  const value = {
    // Generic state (works for both shared and public profile modes)
    isSharedProfileView,  // True for both legacy share and new public profile
    sharedProfileOwnerId, // User ID of profile being viewed
    
    // Legacy shared profile state (share_id based)
    sharedProfileData,
    shareInfo,
    currentShareId,
    
    // New public profile state (user_id based, persistent)
    isPublicProfileView,
    publicProfileUserId,
    profileInfo,
    
    // Computed helpers for components
    isReadOnly: isSharedProfileView || isPublicProfileView,
    
    // Actions - legacy (share_id based)
    enterSharedProfileMode,
    exitSharedProfileMode,
    
    // Actions - new (user_id based, persistent)
    enterPublicProfileMode,
    exitPublicProfileMode
  };

  return (
    <SharedProfileContext.Provider value={value}>
      {children}
    </SharedProfileContext.Provider>
  );
}

export function useSharedProfile() {
  const context = useContext(SharedProfileContext);
  if (!context) {
    throw new Error('useSharedProfile must be used within SharedProfileProvider');
  }
  return context;
}

export default SharedProfileContext;
